import { atom } from 'jotai'
import { atomFamily, atomWithStorage } from 'jotai/utils'
import { PROMPT } from 'constants'

// Define an atom to store the systemPrompt in localStorage
export const promptAtom = atomWithStorage('prompt-9', PROMPT)

// Atom to store the recognized speech transcript
export const transcriptAtom = atom('')

export const aiPhraseAtom = atom('')

// debug options
// export const isDevEnabledAtom = atomWithStorage(false)
export const isDevEnabledAtom = atomWithStorage('isDevEnabled', false)

// Atom to store the status of speech recognition (whether it's listening or not)
export const isListeningAtom = atom(false)

export const pictogramsAtom = atom({})
export const categoriesAtom = atom([])

export const pictogramsFamilyAtom = atomFamily((uuid) =>
  atom(
    (get) => get(pictogramsAtom)[uuid],
    (get, set, updater) => {
      const prev = get(pictogramsAtom)
      const current = prev[uuid]

      // Si `updater` es una función, ejecútala con el estado actual
      const updated =
        typeof updater === 'function'
          ? updater(current)
          : { ...current, ...updater }

      set(pictogramsAtom, {
        ...prev,
        [uuid]: updated,
      })
    }
  )
)

// Step 3: Define the pictogramListAtom to maintain the order of pictograms based on the uuid
// Derived atom for the list of all UUIDs (from pictogramsAtom)
export const pictogramListAtom = atom((get) => {
  const pictograms = get(pictogramsAtom)
  return Object.keys(pictograms) // Get all UUIDs as a list
}) // Store ordered pictogram hashes

export const selectedPictogramsAtom = atom([])

// Derived atom: gets all pictograms based on the ordered list of UUIDs
export const allSelectedPictogramsAtom = atom((get) => {
  const pictogramList = get(selectedPictogramsAtom) // Get the ordered list of UUIDs
  const pictograms = get(pictogramsAtom) // Get the entire pictograms object

  // Return an array of pictograms corresponding to the UUIDs in pictogramList
  return pictogramList.map((uuid) => pictograms[uuid] || null)
})

// Derived atom for the list of visible pictograms (those without a parent)
export const pictogramVisibleListAtom = atom((get) => {
  const pictograms = get(pictogramsAtom)
  const visiblePictograms = Object.values(pictograms).filter(
    (pictogram) => pictogram.visible
  )
  return visiblePictograms.map((pictogram) => pictogram.uuid) // Get UUIDs of visible pictograms
})

// Derived atom to check if the list is not empty
export const hasPictogramsAtom = atom(
  (get) => get(pictogramVisibleListAtom).length > 0
)

// Step 4: Define the historySearchesAtom using atomWithStorage
export const historySearchesAtom = atomWithStorage('historySearches', [])

// Derived atom: gets all pictograms based on the ordered list of UUIDs
export const allPictogramsAtom = atom((get) => {
  const pictogramList = get(pictogramListAtom) // Get the ordered list of UUIDs
  const pictograms = get(pictogramsAtom) // Get the entire pictograms object

  // Return an array of pictograms corresponding to the UUIDs in pictogramList
  return pictogramList.map((uuid) => pictograms[uuid] || null)
})

// Derived atom: gets all pictograms based on the ordered list of UUIDs
export const allVisiblePictogramsAtom = atom((get) => {
  const pictogramVisibleList = get(pictogramVisibleListAtom) // Get the ordered list of UUIDs
  const pictograms = get(pictogramsAtom) // Get the entire pictograms object

  // Return an array of pictograms corresponding to the UUIDs in pictogramList
  return pictogramVisibleList.map((uuid) => pictograms[uuid] || null)
})

// Step 4: Define the historySearchesAtom using atomWithStorage
const defaultTextSettings = {
  position: 'top',
  capitalLetters: false,
  font: 'Roboto',
  color: '#333333',
}

export const textSettingsAtom = atomWithStorage('settings', defaultTextSettings)

export const customImagesAtom = atom({})
// id should be uuid if it's a pictogram or a keyword if image is associated with a keyword

export const selectedVoicesAtom = atomWithStorage('selectedVoices', {})

export const customImagesFamilyAtom = atomFamily((id) =>
  atom(
    (get) => get(customImagesAtom)[id],
    (get, set, arg) => {
      const prev = get(customImagesAtom)
      set(customImagesAtom, { ...prev, [id]: arg })
    }
  )
)

// Define the atom
export const fitzgeraldBackgroundAtom = atomWithStorage(
  'fitzgeraldBackground',
  false
)
export const fitzgeraldFrameAtom = atomWithStorage('fitzgeraldFrame', false)
export const showMiscellaneousPictosAtom = atomWithStorage(
  'showMiscellaneousPictos',
  true
)
export const miscellaneousUpperCaseAtom = atomWithStorage(
  'miscellaneousUpperCase',
  true
)

// Helper function to determine if the device is mobile
const isMobile = window.matchMedia('(max-width: 600px)').matches

// Set default values based on screen size
export const pictoSizeAtom = atom(isMobile ? 100 : 150) // Smaller size for mobile
export const textSizeAtom = atom(isMobile ? 14 : 18) // Smaller text for mobile
